/**
 * Variant Image Filter v7 - Universal Brand Support
 * ==================================================
 * Filters product gallery based on color/finish embedded in image filenames.
 * Supports both parent-child (multi-variant) and single-SKU products.
 *
 * Filename pattern: {brand}_{partnumber}_{finish-slug}_{position}.{ext}
 * Examples:
 *   kohler_k45102_polished-chrome_2.png
 *   delta_83867_matte-black_3.jpg
 *
 * BEHAVIOR:
 * - Single SKU products (1 variant): Show ALL images in gallery
 * - Parent-child products (multiple variants):
 *   - On initial load: Show ONLY position 1 images (swatches for each variant)
 *   - On variant select: Show ONLY images matching the selected finish
 *   - If no finish-specific images exist, show only the variant's associated image from Shopify
 */
(function() {
  'use strict';

  var imageData = [];
  var gallery = null;
  var DEBUG = false;
  var initialized = false;
  var isParentChild = false;
  var productVariants = null; // Cache of product variants from Shopify JSON
  var lastVariantImageUrls = {}; // Cache variant image URLs by finish name
  var filterDebounceTimer = null; // Debounce timer for filter calls

  function log(msg) {
    if (DEBUG) console.log('[VariantFilter] ' + msg);
  }

  function slugify(text) {
    if (!text) return '';
    return text.toLowerCase()
      .trim()
      .replace(/\//g, '-')
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Extract finish slug and position from filename
   */
  function parseFilename(url) {
    if (!url) return { finishSlug: null, position: null };

    var filename = url.split('?')[0].split('/').pop().toLowerCase();
    filename = filename.replace(/_\d+x\d+(\.[a-z]+)?$/, '$1');

    var match = filename.match(/^([a-z]+)_([a-z0-9-]+)_([a-z0-9-]+)_(\d+)(?:_[a-f0-9-]+)?\.[a-z]+$/);

    if (match) {
      return {
        brand: match[1],
        partNumber: match[2],
        finishSlug: match[3],
        position: parseInt(match[4], 10)
      };
    }

    return { finishSlug: null, position: null };
  }

  /**
   * Extract base filename from URL for comparison
   */
  function getBaseFilename(url) {
    if (!url) return '';
    return url.split('?')[0].split('/').pop().toLowerCase().replace(/_\d+x\d+\./, '.');
  }

  /**
   * Load product variants from Shopify's embedded JSON data
   */
  function loadProductVariants() {
    if (productVariants) return productVariants;

    // Try various common locations for product data
    // 1. Global product object
    if (window.product && window.product.variants) {
      productVariants = window.product.variants;
      log('Found product variants in window.product');
      return productVariants;
    }

    // 2. Theme's product data
    if (window.theme && window.theme.product && window.theme.product.variants) {
      productVariants = window.theme.product.variants;
      log('Found product variants in window.theme.product');
      return productVariants;
    }

    // 3. Look for JSON in script tags
    var scripts = document.querySelectorAll('script[type="application/json"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var data = JSON.parse(scripts[i].textContent);
        if (data && data.variants) {
          productVariants = data.variants;
          log('Found product variants in script tag');
          return productVariants;
        }
        if (data && data.product && data.product.variants) {
          productVariants = data.product.variants;
          log('Found product variants in script tag (nested)');
          return productVariants;
        }
      } catch (e) {}
    }

    // 4. Look for data attribute on product form
    var productForm = document.querySelector('[data-product-json], [data-product]');
    if (productForm) {
      var jsonAttr = productForm.getAttribute('data-product-json') || productForm.getAttribute('data-product');
      if (jsonAttr) {
        try {
          var data = JSON.parse(jsonAttr);
          if (data && data.variants) {
            productVariants = data.variants;
            log('Found product variants in data attribute');
            return productVariants;
          }
        } catch (e) {}
      }
    }

    // 5. Look for variant-selection element with product data
    var variantSelection = document.querySelector('variant-selection');
    if (variantSelection) {
      var productAttr = variantSelection.getAttribute('product');
      if (productAttr) {
        try {
          var data = JSON.parse(productAttr);
          if (data && data.variants) {
            productVariants = data.variants;
            log('Found product variants in variant-selection');
            return productVariants;
          }
        } catch (e) {}
      }
    }

    log('Could not find product variants data');
    return null;
  }

  /**
   * Get variant image URL by option value (e.g., "Thunder Grey")
   */
  function getVariantImageByOption(optionValue) {
    var variants = loadProductVariants();
    if (!variants) return null;

    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      // Check if this variant matches the option value
      if (v.options && v.options.indexOf(optionValue) >= 0) {
        // Return the featured image URL if available
        if (v.featured_image && v.featured_image.src) {
          return v.featured_image.src;
        }
        // Some themes use featured_media
        if (v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src) {
          return v.featured_media.preview_image.src;
        }
      }
    }
    return null;
  }

  function detectProductType() {
    var variantSelect = document.querySelector('select[name="id"], variant-selection select');
    if (variantSelect) {
      var options = variantSelect.querySelectorAll('option');
      var realOptions = Array.from(options).filter(function(opt) {
        var text = opt.text.toLowerCase();
        return opt.value && !text.includes('choose') && !text.includes('select');
      });
      return realOptions.length > 1;
    }

    var swatchInputs = document.querySelectorAll('[data-option-value] input, .options-selection__option-value input');
    return swatchInputs.length > 1;
  }

  function init() {
    if (initialized) return;

    gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) {
      log('No gallery found');
      return;
    }

    var figures = gallery.querySelectorAll('[data-gallery-figure]');
    var thumbnails = gallery.querySelectorAll('[data-gallery-thumbnail]');

    log('Gallery found - figures: ' + figures.length + ', thumbnails: ' + thumbnails.length);

    if (figures.length === 0) return;

    isParentChild = detectProductType();
    log('Product type: ' + (isParentChild ? 'Parent-Child (multi-variant)' : 'Single SKU'));

    imageData = [];
    var variantImageCount = 0;

    figures.forEach(function(fig, index) {
      var img = fig.querySelector('img');
      var imgUrl = img ? (img.src || img.dataset.src || '') : '';

      var parsed = parseFilename(imgUrl);
      var isVariantImage = parsed.finishSlug !== null && parsed.position !== null;
      var isPositionOne = parsed.position === 1;

      if (isVariantImage && !isPositionOne) {
        variantImageCount++;
      }

      imageData.push({
        figure: fig,
        thumbnail: thumbnails[index] || null,
        imgUrl: imgUrl,
        baseFilename: getBaseFilename(imgUrl),
        finishSlug: parsed.finishSlug,
        position: parsed.position,
        index: index,
        isVariantImage: isVariantImage,
        isPositionOne: isPositionOne
      });
    });

    log('Processed ' + imageData.length + ' images, ' + variantImageCount + ' are variant images (pos > 1)');

    initialized = true;

    // Remove 'loading' class from gallery navigation so thumbnails become visible
    var navEl = gallery.querySelector('.product-gallery--navigation');
    if (navEl && navEl.classList.contains('loading')) {
      navEl.classList.remove('loading');
      log('Removed loading class from gallery navigation');
    }

    if (isParentChild && variantImageCount > 0) {
      showOnlyPositionOneImages();
    } else {
      log('Single SKU or no variant images - showing all images');
    }

    setupEventListeners();

    // Handle pre-selected variant on initial page load (e.g., direct link via search)
    if (isParentChild && variantImageCount > 0) {
      setTimeout(function() {
        handleInitialVariant();
      }, 200);
    }

  }

  /**
   * Detect if a variant is already selected on page load and filter gallery for it.
   * This handles the case where a user lands directly on a variant URL (e.g., from search).
   */
  function handleInitialVariant() {
    var finishName = null;
    var variantImageUrl = null;

    // 1. Check for variant-selection element with current variant data
    var variantSelection = document.querySelector('variant-selection');
    if (variantSelection) {
      var variantAttr = variantSelection.getAttribute('variant');
      if (variantAttr) {
        try {
          var variant = JSON.parse(variantAttr);
          if (variant && variant.options && variant.options[0]) {
            finishName = variant.options[0];
            variantImageUrl = variant.featured_image ? variant.featured_image.src : null;
            log('Initial variant detected from variant-selection: "' + finishName + '"');
          }
        } catch (e) {}
      }
    }

    // 2. Check for a selected swatch/radio input
    if (!finishName) {
      var checkedInput = document.querySelector('[data-option-value] input:checked, .options-selection__option-value input:checked');
      if (checkedInput && checkedInput.value) {
        finishName = checkedInput.value;
        log('Initial variant detected from checked swatch: "' + finishName + '"');
      }
    }

    // 3. Check for a selected option in dropdown
    if (!finishName) {
      var variantSelect = document.querySelector('select[name="id"], variant-selection select');
      if (variantSelect && variantSelect.selectedOptions[0]) {
        var text = variantSelect.selectedOptions[0].text;
        var lowerText = text.toLowerCase();
        if (text && !lowerText.includes('choose') && !lowerText.includes('select') && variantSelect.selectedOptions[0].value !== '') {
          var dashIndex = text.indexOf(' - $');
          if (dashIndex > 0) text = text.substring(0, dashIndex);
          finishName = text.trim();
          log('Initial variant detected from select dropdown: "' + finishName + '"');
        }
      }
    }

    // 4. Check URL for variant parameter and look it up in product data
    if (!finishName) {
      var urlParams = new URLSearchParams(window.location.search);
      var variantId = urlParams.get('variant');
      if (variantId) {
        var variants = loadProductVariants();
        if (variants) {
          for (var i = 0; i < variants.length; i++) {
            if (variants[i].id.toString() === variantId) {
              finishName = variants[i].options ? variants[i].options[0] : null;
              if (finishName) {
                log('Initial variant detected from URL param: "' + finishName + '"');
              }
              break;
            }
          }
        }
      }
    }

    if (!finishName) {
      log('No pre-selected variant detected on page load');
      return;
    }

    // Cache variant image URL if available
    if (variantImageUrl) {
      lastVariantImageUrls[finishName] = variantImageUrl;
    }

    // Filter images for this finish
    filterByFinishImmediate(finishName);

    // On initial load, we must also select the correct main image since
    // Shopify's native variant switching hasn't fired (no user click).
    // Find the best image to display: prefer the Shopify variant image, then position-1.
    var searchSlug = slugify(finishName);
    var bestIndex = -1;

    // First, try to find the Shopify variant's image (the one linked to the variant)
    if (!variantImageUrl) {
      variantImageUrl = getVariantImageByOption(finishName);
    }
    if (variantImageUrl) {
      var targetBase = getBaseFilename(variantImageUrl);
      for (var j = 0; j < imageData.length; j++) {
        if (imageData[j].baseFilename === targetBase && !imageData[j].figure.classList.contains('vif-hidden')) {
          bestIndex = imageData[j].index;
          break;
        }
      }
    }

    // Fallback: find position-1 image for the finish
    if (bestIndex === -1) {
      for (var k = 0; k < imageData.length; k++) {
        if (imageData[k].finishSlug === searchSlug && imageData[k].position === 1 && !imageData[k].figure.classList.contains('vif-hidden')) {
          bestIndex = imageData[k].index;
          break;
        }
      }
    }

    // Last fallback: first visible image
    if (bestIndex === -1) {
      for (var m = 0; m < imageData.length; m++) {
        if (!imageData[m].figure.classList.contains('vif-hidden')) {
          bestIndex = imageData[m].index;
          break;
        }
      }
    }

    if (bestIndex >= 0) {
      log('Initial load: selecting main image at index ' + bestIndex);
      clickThumbnail(bestIndex);
    }
  }


  function showOnlyPositionOneImages() {
    log('Showing only position 1 images (variant swatches)');
    var shownCount = 0;
    var hiddenCount = 0;
    var firstVisibleIndex = -1;

    imageData.forEach(function(data) {
      var shouldShow = !data.isVariantImage || data.isPositionOne;

      if (shouldShow) {
        showElement(data.figure);
        if (data.thumbnail) showElement(data.thumbnail);
        if (firstVisibleIndex === -1) firstVisibleIndex = data.index;
        shownCount++;
      } else {
        hideElement(data.figure);
        if (data.thumbnail) hideElement(data.thumbnail);
        hiddenCount++;
      }
    });

    log('Showing ' + shownCount + ' images, hiding ' + hiddenCount + ' variant images');
    refreshGalleryScroller();

    if (firstVisibleIndex >= 0) {
      clickThumbnail(firstVisibleIndex);
    }
  }

  /**
   * Show only the variant's associated image from Shopify
   */
  function showVariantImage(variantImageUrl) {
    if (!variantImageUrl) {
      log('No variant image URL provided');
      return false;
    }

    var targetFilename = getBaseFilename(variantImageUrl);
    log('Looking for variant image: ' + targetFilename);

    var foundIndex = -1;

    imageData.forEach(function(data) {
      // Check if this image matches the variant's image
      var isMatch = data.baseFilename === targetFilename ||
                    data.imgUrl.includes(targetFilename.replace(/\.[a-z]+$/, ''));

      if (isMatch) {
        showElement(data.figure);
        if (data.thumbnail) showElement(data.thumbnail);
        if (foundIndex === -1) foundIndex = data.index;
      } else {
        hideElement(data.figure);
        if (data.thumbnail) hideElement(data.thumbnail);
      }
    });

    refreshGalleryScroller();

    if (foundIndex >= 0) {
      log('Found variant image at index ' + foundIndex);
      clickThumbnail(foundIndex);
      return true;
    }

    log('Variant image not found in gallery');
    return false;
  }

  window._showOnlyPositionOneImages = showOnlyPositionOneImages;
  window._showVariantImage = showVariantImage;
  window._variantFilterImageData = function() { return imageData; };

  function setupEventListeners() {
    document.querySelectorAll('select').forEach(function(select) {
      select.addEventListener('change', handleSelectChange);
    });

    // Listen for variant-change events with variant data
    document.addEventListener('variant-change', function(e) {
      if (e.detail && e.detail.variant) {
        var variant = e.detail.variant;
        var finishName = variant.options ? variant.options[0] : null;
        var variantImageUrl = variant.featured_image ? variant.featured_image.src : null;
        filterByFinish(finishName, variantImageUrl);
      }
    });

    var variantSelection = document.querySelector('variant-selection');
    if (variantSelection) {
      variantSelection.addEventListener('variant-change', function(e) {
        if (e.detail && e.detail.variant) {
          var variant = e.detail.variant;
          var finishName = variant.options ? variant.options[0] : null;
          var variantImageUrl = variant.featured_image ? variant.featured_image.src : null;
          filterByFinish(finishName, variantImageUrl);
        }
      });

      var observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.attributeName === 'variant') {
            var variantAttr = variantSelection.getAttribute('variant');
            if (variantAttr) {
              try {
                var variant = JSON.parse(variantAttr);
                var finishName = variant.options ? variant.options[0] : null;
                var variantImageUrl = variant.featured_image ? variant.featured_image.src : null;
                filterByFinish(finishName, variantImageUrl);
              } catch (e) {
                // Fallback to select value
                var select = variantSelection.querySelector('select');
                if (select && select.selectedOptions[0]) {
                  var text = select.selectedOptions[0].text;
                  var colorMatch = text.match(/^([^-$]+)/);
                  if (colorMatch) {
                    filterByFinish(colorMatch[1].trim(), null);
                  }
                }
              }
            }
          }
        });
      });
      observer.observe(variantSelection, { attributes: true });
    }

    document.addEventListener('click', function(e) {
      var label = e.target.closest('.options-selection__option-value-label, .product-form--option-value-label, [data-option-value]');
      if (label) {
        setTimeout(function() {
          var input = label.querySelector('input') || document.getElementById(label.getAttribute('for'));
          if (input && input.value) {
            // Try to get variant image from data attribute or global product data
            var variantImageUrl = null;
            if (window.productData && window.productData.variants) {
              var variant = window.productData.variants.find(function(v) {
                return v.options && v.options[0] === input.value;
              });
              if (variant && variant.featured_image) {
                variantImageUrl = variant.featured_image.src;
              }
            }
            filterByFinish(input.value, variantImageUrl);
          }
        }, 100);
      }
    });

    log('Event listeners attached');
  }

  function handleSelectChange(e) {
    var select = e.target;
    var selectedOption = select.selectedOptions[0];
    if (!selectedOption) return;

    var finishName = selectedOption.text;
    log('Select changed: "' + finishName + '"');

    var lowerText = finishName.toLowerCase();
    if (lowerText.includes('choose') || lowerText.includes('select') || lowerText === '' || selectedOption.value === '') {
      if (isParentChild) {
        showOnlyPositionOneImages();
      }
      return;
    }

    var dashIndex = finishName.indexOf(' - $');
    if (dashIndex > 0) {
      finishName = finishName.substring(0, dashIndex);
    }

    // Try to get variant image URL from data attribute
    var variantImageUrl = selectedOption.dataset ? selectedOption.dataset.image : null;

    filterByFinish(finishName.trim(), variantImageUrl);
  }

  /**
   * Filter gallery by finish name (debounced wrapper)
   * If no finish-specific images exist, fall back to variant's image from Shopify
   */
  function filterByFinish(finishName, variantImageUrl) {
    if (!finishName || !gallery || imageData.length === 0) return;

    // Cache the variant image URL if provided
    if (variantImageUrl) {
      lastVariantImageUrls[finishName] = variantImageUrl;
    }

    // Debounce: cancel previous timer and set new one
    if (filterDebounceTimer) {
      clearTimeout(filterDebounceTimer);
    }

    var cachedFinishName = finishName;
    filterDebounceTimer = setTimeout(function() {
      filterByFinishImmediate(cachedFinishName);
    }, 50);
  }

  /**
   * Actual filter implementation (called after debounce)
   */
  function filterByFinishImmediate(finishName) {
    var searchSlug = slugify(finishName);
    log('Filtering for finish: "' + finishName + '" (slug: "' + searchSlug + '")');

    // Count matching variant images
    var matchCount = 0;
    imageData.forEach(function(data) {
      if (data.finishSlug && data.finishSlug === searchSlug) {
        matchCount++;
      }
    });

    log('Found ' + matchCount + ' matching variant images');

    // If no finish-specific images, use the variant's image from Shopify
    if (matchCount === 0) {
      log('No finish-specific images - looking for variant image from Shopify');

      // Try cached URL first, then look up from product data
      var imageUrl = lastVariantImageUrls[finishName] || getVariantImageByOption(finishName);

      if (imageUrl) {
        log('Found variant image URL: ' + imageUrl.substring(0, 60) + '...');
        if (showVariantImage(imageUrl)) {
          return;
        }
      }

      // If no variant image URL or not found, show only position 1 images
      log('Falling back to position 1 images');
      showOnlyPositionOneImages();
      return;
    }

    // Get the Shopify variant's associated image URL (to keep it visible)
    var variantImageUrl = lastVariantImageUrls[finishName] || getVariantImageByOption(finishName);
    var variantImageBase = variantImageUrl ? getBaseFilename(variantImageUrl) : null;

    // Show images matching the selected finish AND the Shopify variant image
    var position1Index = -1;
    var variantImageIndex = -1;
    var shownCount = 0;
    var hiddenCount = 0;

    imageData.forEach(function(data) {
      var isFinishMatch = data.finishSlug && data.finishSlug === searchSlug;
      var isVariantImage = variantImageBase && data.baseFilename === variantImageBase;

      if (isFinishMatch || isVariantImage) {
        showElement(data.figure);
        if (data.thumbnail) showElement(data.thumbnail);
        shownCount++;

        if (data.position === 1) {
          position1Index = data.index;
        }

        // Track the Shopify variant image index (this will be the main image)
        if (isVariantImage && !isFinishMatch) {
          variantImageIndex = data.index;
          log('Also showing Shopify variant image at index ' + data.index);
        }
      } else {
        hideElement(data.figure);
        if (data.thumbnail) hideElement(data.thumbnail);
        hiddenCount++;
      }
    });

    log('Showing ' + shownCount + ' images (finish + variant), hiding ' + hiddenCount + ' non-matching');
    refreshGalleryScroller();

    // Don't click any thumbnail - let Shopify's native variant switching handle the main image
    // The variant image is already set by Shopify when the user clicks the swatch
  }

  function clickThumbnail(index) {
    // Wait for DOM to settle after show/hide changes
    setTimeout(function() {
      var thumb = gallery.querySelector('[data-gallery-thumbnail][data-gallery-index="' + index + '"]');
      var figure = gallery.querySelector('[data-gallery-figure][data-gallery-index="' + index + '"]');

      if (thumb && !thumb.classList.contains('vif-hidden')) {
        log('Clicking thumbnail at index ' + index);
        thumb.click();

        // Also update main image directly for Empire theme
        updateMainImage(index, figure);
      } else {
        // Fallback: find first visible thumbnail and figure
        log('Thumbnail not found at index ' + index + ', using first visible');
        var visibleThumbs = gallery.querySelectorAll('[data-gallery-thumbnail]');
        for (var i = 0; i < visibleThumbs.length; i++) {
          if (!visibleThumbs[i].classList.contains('vif-hidden')) {
            var visibleIndex = visibleThumbs[i].getAttribute('data-gallery-index');
            visibleThumbs[i].click();
            var visibleFigure = gallery.querySelector('[data-gallery-figure][data-gallery-index="' + visibleIndex + '"]');
            updateMainImage(visibleIndex, visibleFigure);
            break;
          }
        }
      }
    }, 100);
  }

  function updateMainImage(index, figure) {
    // Try to update the main gallery image directly
    try {
      // Find the main image container (Empire theme uses data-gallery-role="main-image" or similar)
      var mainImageContainer = gallery.querySelector('[data-gallery-role="main-image"], .product-gallery--main-image, .gallery--main');

      if (!mainImageContainer && figure) {
        // If no main container found, try to make this figure the "active" one
        var allFigures = gallery.querySelectorAll('[data-gallery-figure]');
        allFigures.forEach(function(f) {
          f.classList.remove('is-active', 'is-selected', 'active');
          f.setAttribute('aria-hidden', 'true');
        });
        figure.classList.add('is-active', 'is-selected', 'active');
        figure.setAttribute('aria-hidden', 'false');
      }

      // Try triggering Empire theme's gallery change event
      var galleryChangeEvent = new CustomEvent('gallery:change', {
        bubbles: true,
        detail: { index: parseInt(index, 10) }
      });
      gallery.dispatchEvent(galleryChangeEvent);

      // Also try Flickity if used
      if (window.Flickity) {
        var flickityInstance = Flickity.data(gallery.querySelector('.flickity-enabled'));
        if (flickityInstance) {
          flickityInstance.select(parseInt(index, 10));
        }
      }
    } catch (e) {
      log('Error updating main image: ' + e.message);
    }
  }

  // Inject CSS — hide class + force gallery navigation to be visible
  var styleTag = document.createElement('style');
  styleTag.textContent =
    '[data-gallery-figure].vif-hidden { display: none !important; }' +
    '[data-gallery-thumbnail].vif-hidden {' +
    '  width: 0 !important; min-width: 0 !important; max-width: 0 !important;' +
    '  height: 0 !important; overflow: hidden !important;' +
    '  padding: 0 !important; margin: 0 !important; border: 0 !important;' +
    '  opacity: 0 !important; pointer-events: none !important;' +
    '}' +
    // Force gallery navigation/thumbnails to be visible regardless of theme CSS
    '.product-gallery--navigation { display: block !important; visibility: visible !important; opacity: 1 !important; height: auto !important; overflow: visible !important; }' +
    '.product-gallery--navigation.loading { display: block !important; visibility: visible !important; opacity: 1 !important; }' +
    '.gallery-navigation--scroller { display: flex !important; visibility: visible !important; opacity: 1 !important; }' +
    '[data-gallery-thumbnail]:not(.vif-hidden) { display: inline-block !important; visibility: visible !important; opacity: 1 !important; }';
  document.head.appendChild(styleTag);

  function showElement(el) {
    if (!el) return;
    el.classList.remove('vif-hidden');
  }

  function hideElement(el) {
    if (!el) return;
    el.classList.add('vif-hidden');
  }

  function refreshGalleryScroller() {
    // No-op: let Empire manage its own layout
  }

  function tryInit() {
    if (!initialized) init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInit);
  } else {
    tryInit();
  }

  setTimeout(tryInit, 100);
  setTimeout(tryInit, 500);
  setTimeout(tryInit, 1000);

  document.addEventListener('shopify:section:load', function() {
    initialized = false;
    tryInit();
  });

  log('Variant Image Filter v7.4 loaded (show variant image + finish images)');
})();
